import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { api, ApiError } from '#/lib/api'
import { useHydratedSession } from '#/lib/auth-client'
import { fmtDateTime } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/cli-auth')({ validateSearch: z.object({ code: z.string().optional() }), component: CliAuth })

type State = { status: 'unknown' | 'pending' | 'confirmed' | 'expired'; expiresAt?: string }

/** Device-flow confirmation for `bystep login` (spec/07 §6). Guests are sent to sign-in and come back with the same code. */
function CliAuth() {
  const { code: initial } = Route.useSearch()
  const navigate = useNavigate()
  const { data: session, isPending } = useHydratedSession()
  const [code, setCode] = useState(initial ?? '')
  const [state, setState] = useState<State | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!isPending && !session?.user) navigate({ to: '/sign-in', search: { redirect: `/cli-auth${initial ? `?code=${initial}` : ''}` } })
  }, [isPending, session, navigate, initial])
  // Inspect the code so the user sees pending/expired before confirming; tick every second for the countdown.
  useEffect(() => {
    if (!session?.user || code.length < 9) { setState(null); return }
    let alive = true
    api<State>(`/api/cli/device/confirm?code=${encodeURIComponent(code)}`).then((s) => { if (alive) setState(s) }).catch(() => {})
    return () => { alive = false }
  }, [session?.user, code])
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const expired = state?.status === 'expired' || (state?.expiresAt ? new Date(state.expiresAt).getTime() <= now : false)
  const remainingMs = state?.expiresAt ? Math.max(0, new Date(state.expiresAt).getTime() - now) : null
  const confirm = async () => {
    setBusy(true)
    try {
      await api('/api/cli/device/confirm', { method: 'POST', json: { code } })
      setDone(true)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'expired') { setState({ status: 'expired' }); toast.error(m.cli_auth_expired()) }
      else if (e instanceof ApiError && e.status === 404) { setState({ status: 'unknown' }); toast.error(m.cli_auth_unknown()) }
      else toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <AppHeader showStats={false} />
      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-4 rounded-2xl border bg-card p-6 text-center" data-testid="cli-auth">
          <h1 className="text-2xl font-bold">{m.cli_auth_title()}</h1>
          {done ? (
            <div className="space-y-2">
              <p className="text-status-done" data-testid="cli-auth-done">{m.cli_auth_done()}</p>
              <p className="text-xs text-muted-foreground">{m.cli_auth_done_hint()}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{m.cli_auth_desc()}</p>
              {session?.user ? <p className="text-xs text-muted-foreground">{m.cli_auth_signed_in_as({ email: session.user.email })}</p> : null}
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="text-center font-mono text-lg tracking-widest" data-testid="cli-auth-code" />
              {state?.status === 'unknown' ? <p className="text-xs text-status-failed">{m.cli_auth_unknown()}</p> : null}
              {state?.status === 'confirmed' ? <p className="text-xs text-muted-foreground">{m.cli_auth_already()}</p> : null}
              {expired ? (
                <p className="text-xs text-status-failed" data-testid="cli-auth-expired">{m.cli_auth_expired()}</p>
              ) : state?.expiresAt ? (
                <p className="text-xs text-muted-foreground" data-testid="cli-auth-expires">
                  {m.cli_auth_expires({ time: fmtDateTime(state.expiresAt) })}
                  {remainingMs !== null ? ` · ${Math.floor(remainingMs / 60000)}:${String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, '0')}` : ''}
                </p>
              ) : null}
              <Button className="w-full" onClick={confirm} disabled={code.length < 9 || busy || expired || state?.status === 'unknown'} data-testid="cli-auth-confirm">{m.cli_auth_confirm()}</Button>
            </>
          )}
        </div>
      </main>
    </>
  )
}
